import {
    Box,
    Center,
    Container,
    Divider,
    Heading,
    SimpleGrid,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Tfoot,
    Th,
    Thead,
    Tr, // Added for potentially better layout than just VStack for multiple county tables
    useColorModeValue // For light/dark mode compatibility
    ,
    VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchFacilitiesCountByCity } from '../../api'; // adjust path if needed
import Loading from '../../components/Loading/Loading';
import countiesCities from "./cities.json"; // adjust path

// --- Data Normalization and Mapping (kept as is, no redesign needed here) ---
const normalizeCityName = {
    "fountain valle": "fountain valley",
    "stantan": "stanton",
    "whitter": "whittier",
    "redandls": "redlands",
    "vistalia": "vista", // Corrected this, it was 'vistalia' in the original but 'vista' in data
    "woodlands hills": "woodland hills",
    "diamond": "diamond bar",
    "lake forest,": "lake forest",
    "laguna woods,": "laguna woods",
    "paramount": "paramount",
    "mission": "mission viejo",
    "pacific": "pacific palisades",
    "rsm": "rancho santa margarita"
};

const buildCityToCounty = (countiesCities) => {
    const map = {};
    Object.entries(countiesCities).forEach(([county, cities]) => {
        cities.forEach(city => {
            map[city.toLowerCase()] = county;
        });
    });
    return map;
};
// --- End Data Normalization and Mapping ---

const FacilitiesStatsByCityPage = () => {
    const { labId } = useParams();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Build city → county map once
    const cityToCounty = buildCityToCounty(countiesCities);

    // Chakra UI color mode hooks
    const headerBg = useColorModeValue('blue.500', 'blue.700');
    const headerColor = useColorModeValue('white', 'gray.100');
    const cardBg = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const stripeColor = useColorModeValue('gray.50', 'gray.800');

    useEffect(() => {
        loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labId]); // Added labId to dependency array, as recommended by eslint

    const loadStats = async () => {
        setLoading(true);
        try {
            const res = await fetchFacilitiesCountByCity(labId);
            if (res.success) {
                // Sort the overall data by count descending for the first table
                const sortedData = res.data.sort((a, b) => b.count - a.count);
                setData(sortedData);
            }
        } catch (err) {
            console.error('Error loading stats:', err);
            // Optionally, set an error state to display a message to the user
        }
        setLoading(false);
    };

    // Group data by county and sort cities within each county
    const countyCityCounts = {};
    data.forEach(item => {
        const cityRaw = normalizeCityName[item._id.city] || item._id.city;
        const city = cityRaw?.toLowerCase();
        const county = cityToCounty[city] || 'Unknown County'; // Fallback for unknown cities
        if (!countyCityCounts[county]) countyCityCounts[county] = [];
        countyCityCounts[county].push({ city: cityRaw, count: item.count });
    });

    // Sort counties alphabetically and then cities alphabetically within each county
    const sortedCountyCityCounts = Object.entries(countyCityCounts)
        .sort(([countyA], [countyB]) => countyA.localeCompare(countyB))
        .map(([county, cities]) => ({
            county,
            cities: cities.sort((a, b) => a.city.localeCompare(b.city))
        }));


    if (loading) {
        return (
            <Center minH="calc(100vh - 100px)"> {/* Full viewport height - some header/footer space */}
                <Loading />
            </Center>
        );
    }

    // Handle case where no data is loaded
    if (!data || data.length === 0) {
        return (
            <Center minH="calc(100vh - 100px)">
                <Text fontSize="xl" color="gray.500">No facility data available for this lab ID.</Text>
            </Center>
        );
    }

    return (
        <Container maxW="7xl" py={{ base: 8, md: 12 }}>
            <VStack spacing={{ base: 10, md: 16 }} align="stretch">

                {/* Section 1: Overall Facility Counts by City */}
                <Box>
                    <Heading as="h1" size="xl" mb={6} color={headerBg} textAlign="center">
                        Facility Counts by City (All)
                    </Heading>
                    <TableContainer
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="xl"
                        shadow="lg"
                        maxH="600px" // Increased max height for more visibility
                        overflowY="auto"
                        bg={cardBg}
                    >
                        <Table variant="simple" size="md">
                            <Thead bg={headerBg}>
                                <Tr>
                                    <Th color={headerColor}>#</Th>
                                    <Th color={headerColor}>City</Th>
                                    <Th isNumeric color={headerColor}>Facility Count</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {data.map((item, idx) => (
                                    <Tr key={item._id.city || idx} _odd={{ bg: stripeColor }}>
                                        <Td>{idx + 1}</Td>
                                        <Td>{(normalizeCityName[item._id.city] || item._id.city) || 'Unknown'}</Td>
                                        <Td isNumeric>{item.count}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                            <Tfoot>
                                <Tr>
                                    <Th colSpan={2} fontSize="lg">Total Facilities</Th>
                                    <Th isNumeric fontSize="lg">
                                        {data.reduce((sum, item) => sum + item.count, 0)}
                                    </Th>
                                </Tr>
                            </Tfoot>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider borderColor={borderColor} />

                {/* Section 2: Facility Counts Grouped by County & City */}
                <Box>
                    <Heading as="h2" size="xl" mb={8} color={headerBg} textAlign="center">
                        Facility Counts by County & City
                    </Heading>
                    {/* Use SimpleGrid for a responsive grid of county tables */}
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                        {sortedCountyCityCounts.map(({ county, cities }) => (
                            <Box
                                key={county}
                                border="1px solid"
                                borderColor={borderColor}
                                borderRadius="xl"
                                shadow="md"
                                bg={cardBg}
                                p={4}
                            >
                                <Heading as="h3" size="md" mb={4} color="blue.600">
                                    {county}
                                </Heading>
                                <TableContainer maxH="400px" overflowY="auto">
                                    <Table variant="striped" colorScheme="gray" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>#</Th>
                                                <Th>City</Th>
                                                <Th isNumeric>Count</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {cities.map((cityObj, idx) => (
                                                <Tr key={cityObj.city}>
                                                    <Td>{idx + 1}</Td>
                                                    <Td>{cityObj.city}</Td>
                                                    <Td isNumeric>{cityObj.count}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                        <Tfoot>
                                            <Tr>
                                                <Th colSpan={2}>Total ({county.replace(' County', '')})</Th>
                                                <Th isNumeric>
                                                    {cities.reduce((sum, c) => sum + c.count, 0)}
                                                </Th>
                                            </Tr>
                                        </Tfoot>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>
            </VStack>
        </Container>
    );
};

export default FacilitiesStatsByCityPage;